import { useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Clock, MoreVertical, Plus, X } from "lucide-react";
import {
  DREAM_STAGES,
  DreamStage,
  addStep,
  doneSteps,
  editStep,
  openSteps,
  setStepDone,
  type Step,
} from "../utils/dreamCompanies";
import { formatAppDate } from "./applicationShared";
import { MENU_ITEM, OUTLINE_TAG, TAG } from "./applicationShared";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { MELT_BUTTON, MELT_BUTTON_QUIET, MELT_DIALOG, MELT_REQUIRED } from "./ui/melt";
import { cn } from "../lib/utils";

// Pieces shared by the dream company cards, the company side sheet and their forms.

/** Melt dialog shell for the company / role / person forms (same layout as Add application). */
export function FormDialog({
  open,
  onClose,
  title,
  description,
  submitLabel,
  canSubmit,
  submitting = false,
  onSubmit,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: React.ReactNode;
  submitLabel: string;
  canSubmit: boolean;
  submitting?: boolean;
  onSubmit: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent showClose={false} className={cn(MELT_DIALOG, className)}>
        <div className="flex items-start justify-between gap-4 px-7 pt-6">
          <div>
            <DialogTitle className="text-[22px] font-semibold tracking-[-.02em] text-melt-text1">{title}</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-melt-text2">{description}</DialogDescription>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 grid h-8 w-8 flex-none place-items-center rounded-lg text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !submitting) onSubmit();
          }}
          className="flex flex-col gap-5 px-7 pb-6 pt-5"
        >
          {children}
          <div className="mt-1 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} disabled={submitting} className={MELT_BUTTON_QUIET}>
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit || submitting} className={MELT_BUTTON}>
              {submitLabel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Label + control, stacked like the other melt forms. */
export function Field({
  id,
  label,
  required,
  hint,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <label htmlFor={id} className="text-sm font-semibold text-melt-text1">
        {label}
        {required && <span aria-hidden className={MELT_REQUIRED}>*</span>}
      </label>
      {children}
      {hint && <p className="-mt-0.5 text-[12.5px] text-melt-text3">{hint}</p>}
    </div>
  );
}

/** Google's favicon service (keyless); lettered tile when there's no usable domain. */
/**
 * A company's logo: the image its job board gives (`logo`), else its
 * website's icon (`domain`), else its initials.
 */
export function CompanyLogo({
  name,
  domain,
  logo,
  size = 40,
}: {
  name: string;
  domain?: string;
  logo?: string | null;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  const [logoTries, setLogoTries] = useState(0);
  const validDomain = domain && domain.includes(".") ? domain : undefined;
  const validLogo = logo && logo.startsWith("https://") ? logo : undefined;
  const initials = name
    .trim()
    .split(/\s+/)
    .map((s) => s[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

  useEffect(() => setFailed(false), [validDomain]);
  useEffect(() => setLogoTries(0), [validLogo]);

  if (validLogo && logoTries < 2) {
    return (
      <span
        className="grid flex-none place-items-center overflow-hidden border border-melt-hair bg-white"
        style={{ width: size, height: size, borderRadius: Math.round(size * 0.25) }}
      >
        <img
          // Some careers sites (Workday) send a good image marked as a download, which
          // browsers won't show: try it once through an image proxy that labels it properly
          src={logoTries === 0 ? validLogo : `https://wsrv.nl/?url=${encodeURIComponent(validLogo)}`}
          alt=""
          onError={() => setLogoTries((tries) => tries + 1)}
          className="object-contain"
          style={{ width: size * 0.8, height: size * 0.8 }}
        />
      </span>
    );
  }

  if (validDomain && !failed) {
    return (
      <span
        className="grid flex-none place-items-center overflow-hidden border border-melt-hair bg-white"
        style={{ width: size, height: size, borderRadius: Math.round(size * 0.25) }}
      >
        <img
          src={`https://www.google.com/s2/favicons?domain=${validDomain}&sz=128`}
          alt=""
          onError={() => setFailed(true)}
          className="object-contain"
          style={{ width: size * 0.6, height: size * 0.6 }}
        />
      </span>
    );
  }

  return (
    <span
      className="grid flex-none place-items-center bg-melt-peach font-semibold text-melt-amber"
      style={{ width: size, height: size, fontSize: size * 0.4, borderRadius: Math.round(size * 0.25) }}
    >
      {initials || <Building2 size={size * 0.45} strokeWidth={1.75} />}
    </span>
  );
}

/** "Closes in 3d" inside a week, "21d left" after that. */
export function DeadlineChip({ days }: { days: number }) {
  const soon = days <= 7;
  return (
    <span
      className={cn(
        "inline-flex flex-none items-center gap-1 whitespace-nowrap",
        soon ? cn(TAG, "bg-melt-peach text-[#8A5210]") : OUTLINE_TAG
      )}
    >
      <Clock size={11} strokeWidth={2} />
      {days === 0 ? "Closes today" : soon ? `Closes in ${days}d` : `${days}d left`}
    </span>
  );
}

/** Research → Connect → Apply → Interview, one segment per step. */
export function StageBar({ reached }: { reached: Record<DreamStage, boolean> }) {
  return (
    <div className="grid grid-cols-4 gap-1.5" aria-label="Progress">
      {DREAM_STAGES.map(({ id, label }) => (
        <div key={id}>
          <div className={cn("h-[3px] rounded-full", reached[id] ? "bg-melt-caramel" : "bg-melt-track")} />
          <div
            className={cn(
              "mt-1.5 text-[11.5px]",
              reached[id] ? "font-medium text-melt-text1" : "text-melt-text3"
            )}
          >
            {label}
            <span className="sr-only">{reached[id] ? " (done)" : ""}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Small uppercase heading over a sheet section, like the tracker's "Emails · 3". */
export function SectionLabel({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="flex items-center gap-2 font-plex text-[10.5px] font-medium uppercase tracking-[.1em] text-melt-text3">
      {children}
      {typeof count === "number" && count > 0 && <span>· {count}</span>}
    </div>
  );
}

export const DANGER_ITEM = cn(MENU_ITEM, "text-[#A5492E] focus:bg-[#A5492E]/[.07] [&_svg]:text-[#A5492E]");

/** The ⋮ menu on a card, a sheet row or the sheet header. */
export function RowMenu({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={label}
          className="grid h-6 w-6 flex-none place-items-center rounded-[7px] text-melt-text3 outline-none hover:bg-melt-text1/[.07] hover:text-melt-text1 focus-visible:ring-2 focus-visible:ring-melt-caramel/30 data-[state=open]:bg-melt-text1/[.07]"
        >
          <MoreVertical size={15} strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// --- Next steps ----------------------------------------------------------------

// Clicks on a step's checkbox, text or field shouldn't also open the company
// sheet underneath. Only those: empty space in the row still opens it.
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const STEP_INPUT =
  "min-w-0 flex-1 border-b border-melt-hair bg-transparent pb-1 text-[13.5px] text-melt-text1 outline-none transition-colors placeholder:text-melt-text3 focus:border-melt-caramel";

/**
 * One step: tick it off (it strikes through for a beat, then moves to Done),
 * untick a done one, or click the text to edit it. Clearing the text deletes it.
 */
export function StepRow({
  step,
  onToggle,
  onEdit,
}: {
  step: Step;
  onToggle: (done: boolean) => void;
  onEdit: (text: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(step.text);
  const [finishing, setFinishing] = useState(false);
  const timer = useRef<number>();

  useEffect(() => {
    setDraft(step.text);
    setFinishing(false);
  }, [step.text, step.done]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const checked = step.done || finishing;
  const toggle = () => {
    if (step.done) return onToggle(false);
    setFinishing(true);
    timer.current = window.setTimeout(() => onToggle(true), 450);
  };
  const commit = () => {
    setEditing(false);
    if (draft.trim() !== step.text) onEdit(draft);
  };

  return (
    <div className="flex min-h-[22px] items-start gap-2">
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          toggle();
        }}
        disabled={finishing}
        aria-label={step.done ? `Mark "${step.text}" not done` : `Mark "${step.text}" done`}
        title={step.done ? "Untick" : "Done"}
        className={cn(
          "mt-[2px] grid h-4 w-4 flex-none place-items-center rounded-[5px] border transition-colors",
          checked
            ? "border-melt-caramel bg-melt-caramel text-white hover:bg-melt-press"
            : "border-melt-oat bg-white hover:border-melt-caramel"
        )}
      >
        {checked && <Check size={11} strokeWidth={2.5} />}
      </button>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(step.text);
              setEditing(false);
            }
          }}
          aria-label="Edit step"
          onClick={stop}
          className={STEP_INPUT}
        />
      ) : (
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            setEditing(true);
          }}
          title="Edit"
          className={cn(
            "min-w-0 flex-1 text-left text-[13.5px] leading-snug line-clamp-2 transition-colors",
            checked ? "text-melt-text3 line-through" : "text-melt-text1 hover:text-melt-text2"
          )}
        >
          {step.text}
        </button>
      )}
      {step.done && step.doneAt && (
        <span className="mt-[2px] flex-none font-plex text-[11px] text-melt-text3">{formatAppDate(step.doneAt)}</span>
      )}
    </div>
  );
}

/** "+ Add next step" that opens a field. Enter adds and keeps it open for another one. */
export function AddStep({ onAdd }: { onAdd: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const add = () => {
    if (draft.trim()) onAdd(draft);
    setDraft("");
  };

  return (
    <div className="flex min-h-[22px] items-start gap-2">
      {open ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            add();
            setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
            if (e.key === "Escape") {
              setDraft("");
              setOpen(false);
            }
          }}
          placeholder="What’s next for this company?"
          aria-label="New next step"
          onClick={stop}
          className={STEP_INPUT}
        />
      ) : (
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            setOpen(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-md text-[13px] font-medium text-melt-text3 transition-colors hover:text-melt-text1"
        >
          <Plus size={14} strokeWidth={2} />
          Add next step
        </button>
      )}
    </div>
  );
}

type StepsProps = { steps: Step[]; onChange: (steps: Step[]) => void };

/** Side panel: every open step, "+ Add next step", then a collapsed "Done · N" list you can untick from. */
export function NextSteps({ steps, onChange }: StepsProps) {
  const [showDone, setShowDone] = useState(false);
  const open = openSteps(steps);
  const done = doneSteps(steps);
  const row = (step: Step) => (
    <StepRow
      key={step.id}
      step={step}
      onToggle={(isDone) => onChange(setStepDone(steps, step.id, isDone))}
      onEdit={(text) => onChange(editStep(steps, step.id, text))}
    />
  );

  return (
    <div>
      <div className="space-y-2">
        {open.map(row)}
        <AddStep onAdd={(text) => onChange(addStep(steps, text))} />
      </div>
      {done.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            aria-expanded={showDone}
            className="inline-flex items-center gap-1 text-[12.5px] font-medium text-melt-text3 transition-colors hover:text-melt-text1"
          >
            <ChevronDown size={14} strokeWidth={2} className={cn("transition-transform duration-150", !showDone && "-rotate-90")} />
            Done · {done.length}
          </button>
          {showDone && <div className="mt-2 space-y-2">{done.map(row)}</div>}
        </div>
      )}
    </div>
  );
}

/** Card: the first open step and "+N more", or "+ Add next step". */
export function CardSteps({ steps, onChange }: StepsProps) {
  const open = openSteps(steps);
  if (!open.length) return <AddStep onAdd={(text) => onChange(addStep(steps, text))} />;
  const [first, ...rest] = open;
  return (
    <div>
      <StepRow
        step={first}
        onToggle={(isDone) => onChange(setStepDone(steps, first.id, isDone))}
        onEdit={(text) => onChange(editStep(steps, first.id, text))}
      />
      {rest.length > 0 && <p className="mt-1 pl-6 text-[12px] text-melt-text3">+{rest.length} more</p>}
    </div>
  );
}
